import { BulletPointTextarea, Form, TextField } from "@/base";
import { Checkbox } from "@heroui/react";
import { joiResolver } from "@hookform/resolvers/joi";
import { validationOptions } from "@sr-partner-portal/common";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MonthRangePicker } from "../components/month-range-picker";
import type { JobExperience, Training } from "../types";
import {
    jobExperienceValidationSchema,
    trainingValidationSchema,
} from "../validation";

type FormData = JobExperience | Training;

interface ExperienceFormProps {
    isOpen: boolean;
    onClose: () => void;
    onFormSubmit: (data: FormData) => void;
    initialData?: Partial<FormData>;
    type: "experience" | "training";
}

const ExperienceForm = ({
    isOpen,
    onClose,
    onFormSubmit,
    initialData,
    type,
}: ExperienceFormProps) => {
    const formController = useForm<FormData>({
        resolver: joiResolver(
            type === "experience"
                ? jobExperienceValidationSchema
                : trainingValidationSchema,
            validationOptions,
        ),
        defaultValues: initialData || {
            isCurrent: false,
        },
    });

    const isCurrent = formController.watch("isCurrent");

    useEffect(() => {
        if (isOpen) {
            return;
        }

        formController.reset();
    }, [isOpen, formController.reset]);

    const handleMonthRangeChange = (start: Date, end: Date) => {
        formController.setValue("startDate", start);
        formController.setValue("endDate", end);
    };

    const handleCurrentChange = (isCurrent: boolean) => {
        formController.setValue("isCurrent", isCurrent);
    };

    const handleSubmit = (data: FormData) => {
        onFormSubmit(data);
        formController.reset();
        onClose();
    };

    const startDate = useMemo(() => {
        const startDate = formController.getValues("startDate");
        return startDate ? new Date(startDate) : new Date();
    }, [formController]);

    const endDate = useMemo(() => {
        if (isCurrent) {
            return new Date();
        }

        const endDate = formController.getValues("endDate");
        return endDate ? new Date(endDate) : new Date();
    }, [formController, isCurrent]);

    // show description input if description set or clicked button
    const [showDescription, setShowDescription] = useState(
        !!initialData?.description,
    );

    return (
        <Form
            controller={formController}
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
            id="experience-form"
        >
            <TextField<FormData>
                fieldName="company"
                label={
                    type === "experience"
                        ? "Firma/Organisation"
                        : "Unternehmen/Universität"
                }
                isRequired={true}
                min={3}
                size="lg"
            />

            <TextField<FormData>
                fieldName={type === "experience" ? "position" : "title"}
                label={
                    type === "experience"
                        ? "Position"
                        : "Bezeichnung ggf. inkl. Fachrichtung"
                }
                isRequired={true}
                size="lg"
            />
            <Checkbox
                isSelected={showDescription}
                onValueChange={setShowDescription}
            >
                Beschreibung hinzufügen
            </Checkbox>
            {showDescription && (
                <BulletPointTextarea
                    fieldName="description"
                    label="Beschreibung"
                    className="min-h-32"
                    tiptapClassName="text-base"
                    withNestedBulletPoints={false}
                />
            )}
            <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                    <MonthRangePicker
                        showCurrentCheckbox={true}
                        startValue={startDate}
                        endValue={endDate}
                        maxEndValue={new Date()}
                        onChange={handleMonthRangeChange}
                        onCurrentChange={handleCurrentChange}
                        isCurrent={isCurrent}
                    />
                </div>
            </div>
        </Form>
    );
};

export { ExperienceForm };
